import React from 'react';
import Error from 'next/error';
import {
    logger,
    Utils,
    Store,
    StoreDeliveryThreshold,
    Translations,
    TranslationPages,
    Pages,
} from '@deporvillage/frontend-common';
import Product from '@components/product/Product';
import Catalog from '@data-service/controller/catalogController';
import Translation from '@data-service/controller/translationController';
import Configuration from '@data-service/controller/configurationController';
import ConfigTemplate from '@templates/Config';
import PageTemplate from '@templates/Page';
import ProductTemplate from '@templates/Product';
import { getContext } from '@utils/context';
import HttpHeaderNames from '@utils/headers';
import client from '@client/client';
import { Layout as LayoutQuery } from '@query/layout';
import {
    Customer_Review_GetProductReviewCount as ProductReviewCountQuery,
    Customer_Review_GetProductReviewRating as ProductReviewRatingQuery,
    Customer_Review_ListCatalogProductReviews as ProductReviewsQuery,
} from '@query/customer/reviews';

logger.setLevel('WARN');

const ProductPage = ({
    model, i18n, config, layout, err,
}) => (
    err ? (
        <Error statusCode={err.statusCode} />
    ) : (
        <ConfigTemplate i18n={i18n} config={config}>
            <PageTemplate model={{ layout }}>
                <ProductTemplate model={model}>
                    <Product model={model} />
                </ProductTemplate>
            </PageTemplate>
        </ConfigTemplate>
    )
);

export async function getServerSideProps(context) {
    const {
        session,
        store,
        storeUuid,
        lang,
        locale,
        country,
        url,
        geoIp,
        isBot,
    } = await getContext(context);

    if (!Store.isValidLang(lang)) {
        context.res.statusCode = 400;
        return { props: { err: { statusCode: 400, message: 'Invalid language' } } };
    }

    const [
        model,
        config,
        productConfig,
        i18n,
        i18nGeoIp,
        layoutRes,
    ] = await Promise.all([
        Catalog.getProduct({
            session,
            lang,
            id: context?.params?.id,
            country,
            store,
            locale,
        }),
        Configuration.getAll({
            session,
            store,
            lang,
            country,
            locale,
        }),
        Configuration.getProduct({
            session,
            store,
        }),
        Translation.get({
            session,
            locale,
            keys: Translations.getKeys(TranslationPages.Product),
        }),
        geoIp && geoIp.locale ?
            Translation.get({
                session,
                locale: geoIp.locale,
                keys: 'geo_ip',
            }) : {},
        client.query(LayoutQuery, {}, {
            [HttpHeaderNames.PlatformStoreUuid]: storeUuid,
            [HttpHeaderNames.RequestId]: session?.requestId,
        }),
    ]);

    if (!model) {
        context.res.statusCode = 404;
        return { props: { err: { statusCode: 404 } } };
    }

    const productRelatedIds = model?.related_products?.[0]?.items?.map((item) => item?.uuid)?.join(':');

    const [
        reviewsRes,
        reviewsRatingRes,
        reviewsRelatedCountRes,
    ] = await Promise.all([
        client.query(ProductReviewsQuery, {
            productId: model?.uuid,
            page: 1,
            limit: 15,
            store: storeUuid,
        }, {
            [HttpHeaderNames.RequestId]: session?.requestId,
            [HttpHeaderNames.Country]: country,
            [HttpHeaderNames.Language]: lang,
        }),
        client.query(ProductReviewRatingQuery, {
            productId: model?.uuid,
            store: storeUuid,
        }, {
            [HttpHeaderNames.RequestId]: session?.requestId,
            [HttpHeaderNames.Country]: country,
            [HttpHeaderNames.Language]: lang,
        }),
        (productRelatedIds && client.query(ProductReviewCountQuery, {
            productIds: (model?.related_products[0]?.items || [])?.map((item) => item?.uuid)?.join(':'),
            store: storeUuid,
        }, {
            [HttpHeaderNames.RequestId]: session?.requestId,
            [HttpHeaderNames.Country]: country,
            [HttpHeaderNames.Language]: lang,
        })) || null,
    ]);

    const {
        CategoryMenu,
        GetPageConfigFlags,
        GetPageAcceptanceMarks,
        GetStores,
        Customer_Review_ListProductReviews: ProductReviews,
        Customer_Review_GetProductReviewCount: ProductReviewCount,
        Customer_Review_GetProductReviewRating: ProductReviewRating,
    } = {
        ...layoutRes?.data || {},
        ...reviewsRes?.data || {},
        ...reviewsRatingRes?.data || {},
        ...reviewsRelatedCountRes?.data || {},
    };

    return {
        props: {
            model: {
                ...model,
                reviews: ProductReviews || null,
                reviewsRelated: {
                    pagination: {
                        totalCount: ProductReviewCount?.totalCount || 0,
                    },
                },
                rating: {
                    rate: ProductReviewRating?.rating || 0,
                    count: ProductReviewRating?.count || 0,
                },
            },
            config: {
                ...config,
                ...productConfig,
                geoIp,
                lang,
                klarna: null,
                locale,
                storeId: store,
                page: Pages.Product,
                url,
                isBot,
                environment: process.env.ENVIRONMENT || 'dev',
                freeDeliveryThreshold: StoreDeliveryThreshold[locale],
                extension: Store.getExtension(locale),
            },
            i18n: {
                ...i18n,
                ...i18nGeoIp,
            },
            layout: {
                nav: Utils.getMenu(CategoryMenu, model?.root_category),
                stores: GetStores,
                icons: GetPageAcceptanceMarks,
                ...GetPageConfigFlags,
            },
        },
    };
}

export default ProductPage;
